import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { FiSearch, FiDownload, FiDollarSign, FiCheckCircle, FiClock, FiFileText, FiTrash2, FiRotateCcw } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { formatarData } from '../utils/dateUtils';
import { observacoesIndicaCarne } from '../constants/formasPagamento';

const Pagamentos = () => {
  const [pagamentos, setPagamentos] = useState([]);
  const [viagens, setViagens] = useState([]);
  const [estatisticas, setEstatisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtroViagem, setFiltroViagem] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroStatusCarne, setFiltroStatusCarne] = useState('');
  const [apenasCarne, setApenasCarne] = useState(false);
  const [zerando, setZerando] = useState(false);

  useEffect(() => {
    fetchViagens();
    fetchEstatisticas();
  }, []);

  // Só busca dados quando uma viagem estiver selecionada (receita por viagem)
  useEffect(() => {
    if (filtroViagem) {
      fetchPagamentos();
      fetchEstatisticas();
    } else {
      setLoading(false);
      setPagamentos([]);
      setEstatisticas({
        pago: 0, pendente: 0, parcial: 0, total: 0,
        carne: { total_a_receber: 0, total_recebido: 0, parcelas_pendentes: 0, quantidade_carnes: 0 }
      });
    }
  }, [filtroViagem, filtroStatus, filtroStatusCarne, apenasCarne]);

  const fetchPagamentos = async () => {
    try {
      const params = new URLSearchParams();
      if (filtroViagem) params.append('viagem_id', filtroViagem);
      if (filtroStatus) params.append('status', filtroStatus);
      if (filtroStatusCarne) params.append('status_carne', filtroStatusCarne);
      if (apenasCarne) params.append('apenas_carne', 'true');

      const response = await api.get(`/api/pagamentos?${params}`);
      setPagamentos(response.data);
    } catch (error) {
      toast.error('Erro ao carregar pagamentos');
    } finally {
      setLoading(false);
    }
  };

  const fetchViagens = async () => {
    try {
      const response = await api.get('/api/viagens');
      setViagens(response.data);
    } catch (error) {
      console.error('Erro ao carregar viagens');
    }
  };

  const fetchEstatisticas = async () => {
    try {
      const params = new URLSearchParams();
      if (filtroViagem) params.append('viagem_id', filtroViagem);

      const response = await api.get(`/api/pagamentos/estatisticas?${params}`);
      setEstatisticas(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas');
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filtroViagem) params.append('viagem_id', filtroViagem);

      const response = await api.get(`/api/export/financeiro/excel?${params}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'relatorio_financeiro.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Relatório exportado com sucesso');
    } catch (error) {
      toast.error('Erro ao exportar relatório');
    }
  };

  const handleExportPDF = async () => {
    if (!filtroViagem) {
      toast.error('Selecione uma viagem para exportar o relatório em PDF');
      return;
    }

    try {
      const response = await api.get(`/api/export/pagamentos/pdf?viagem_id=${filtroViagem}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.download = `relatorio_pagamentos_viagem_${filtroViagem}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Relatório PDF gerado com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao gerar relatório PDF');
    }
  };

  const handleReembolsarPagamentoAvista = async (pagamento) => {
    const isCarne = observacoesIndicaCarne(pagamento.observacoes);
    if (isCarne) {
      toast.error('Para carnê, use Gerenciar parcelas no cadastro do passageiro.');
      return;
    }
    if (pagamento.status !== 'pago') {
      toast.error('Somente pagamentos "pago" podem ser reembolsados.');
      return;
    }
    const obs = window.prompt(
      'Observações do reembolso (opcional). O valor sai do Total Pago.',
      ''
    );
    if (obs === null) return;
    try {
      await api.post(`/api/pagamentos/${pagamento.id}/reembolsar`, {
        observacoes: obs || undefined,
      });
      toast.success('Reembolso registrado');
      fetchPagamentos();
      fetchEstatisticas();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao registrar reembolso');
    }
  };

  const handleZerarViagem = async () => {
    if (!filtroViagem) return;
    const viagemSelecionada = viagens.find((v) => v.id === parseInt(filtroViagem, 10));
    const msg = viagemSelecionada
      ? `Zerar pagamentos e parcelas da viagem "${viagemSelecionada.titulo}" e liberar todas as poltronas? Os passageiros NÃO serão excluídos (continuarão na base para futuros pacotes). Isso não pode ser desfeito.`
      : 'Zerar dados financeiros e liberar poltronas desta viagem? Os passageiros não serão excluídos. Isso não pode ser desfeito.';
    if (!window.confirm(msg)) return;
    setZerando(true);
    try {
      await api.delete(`/api/pagamentos/zerar-viagem/${filtroViagem}`);
      toast.success('Dados financeiros zerados e poltronas liberadas. Passageiros mantidos na base.');
      fetchPagamentos();
      fetchEstatisticas();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao zerar dados da viagem');
    } finally {
      setZerando(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Controle Financeiro</h1>
          <p className="text-white mt-1">Gerencie pagamentos e receitas</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExportPDF}
            disabled={!filtroViagem}
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title={!filtroViagem ? 'Selecione uma viagem para exportar PDF' : 'Exportar relatório em PDF'}
          >
            <FiFileText className="mr-2" size={20} />
            Exportar PDF
          </button>
          <button
            onClick={handleExport}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <FiDownload className="mr-2" size={20} />
            Exportar Excel
          </button>
        </div>
      </div>

      {/* Aviso: receita por viagem */}
      {!filtroViagem && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
          <p className="font-medium">Controle por viagem</p>
          <p className="text-sm mt-1">
            Selecione uma viagem no filtro acima para ver a receita e os pagamentos <strong>apenas dessa viagem</strong>. 
            Cada viagem tem controle financeiro independente. Para o mesmo destino em nova data, cadastre uma nova viagem em Viagens — ela começará com R$ 0,00.
          </p>
        </div>
      )}

      {/* Estatísticas Gerais */}
      {estatisticas && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-sky-50 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-700">Total Pago</p>
                <p className="text-3xl font-bold text-green-600 mt-2">
                  R$ {(estatisticas.pago || 0).toFixed(2)}
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <FiCheckCircle className="text-green-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-sky-50 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-700">Parcial</p>
                <p className="text-3xl font-bold text-blue-600 mt-2">
                  R$ {(estatisticas.parcial || 0).toFixed(2)}
                </p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <FiDollarSign className="text-blue-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-sky-50 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-700">Total Pendente</p>
                <p className="text-3xl font-bold text-yellow-600 mt-2">
                  R$ {(estatisticas.pendente || 0).toFixed(2)}
                </p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-full">
                <FiClock className="text-yellow-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-sky-50 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-700">Receita Total</p>
                <p className="text-3xl font-bold text-primary-600 mt-2">
                  R$ {(estatisticas.total || 0).toFixed(2)}
                </p>
              </div>
              <div className="bg-primary-100 p-3 rounded-full">
                <FiDollarSign className="text-primary-600" size={24} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Estatísticas do Carnê */}
      {estatisticas && estatisticas.carne && estatisticas.carne.quantidade_carnes > 0 && (
        <div className="bg-purple-50 rounded-lg shadow p-6 border-2 border-purple-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4">📋 Controle de Carnê</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg p-4">
              <p className="text-xs text-gray-600">Total a Receber (Carnê)</p>
              <p className="text-2xl font-bold text-purple-600 mt-1">
                R$ {(estatisticas.carne.total_a_receber || 0).toFixed(2)}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <p className="text-xs text-gray-600">Total Recebido (Carnê)</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                R$ {(estatisticas.carne.total_recebido || 0).toFixed(2)}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <p className="text-xs text-gray-600">Parcelas Pendentes</p>
              <p className="text-2xl font-bold text-yellow-600 mt-1">
                {estatisticas.carne.parcelas_pendentes || 0}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4">
              <p className="text-xs text-gray-600">Carnês Ativos</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">
                {estatisticas.carne.quantidade_carnes || 0}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-sky-50 rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <select
            value={filtroViagem}
            onChange={(e) => setFiltroViagem(e.target.value)}
            className="px-4 py-2 bg-white border border-sky-200 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400 text-gray-800"
          >
            <option value="">Selecione uma viagem</option>
            {[...viagens]
              .sort((a, b) => new Date(b.data_ida || 0) - new Date(a.data_ida || 0))
              .map((v) => (
                <option key={v.id} value={v.id}>{v.titulo}</option>
              ))}
          </select>
          <select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
            className="px-4 py-2 bg-white border border-sky-200 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400 text-gray-800"
          >
            <option value="">Todos os status</option>
            <option value="pago">Pago</option>
            <option value="pendente">Pendente</option>
            <option value="parcial">Parcial</option>
            <option value="desistencia">Desistência</option>
            <option value="cancelado">Cancelado</option>
            <option value="reembolsado">Reembolsado</option>
          </select>
          <select
            value={filtroStatusCarne}
            onChange={(e) => setFiltroStatusCarne(e.target.value)}
            className="px-4 py-2 bg-white border border-sky-200 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400 text-gray-800"
          >
            <option value="">Status do Carnê</option>
            <option value="pendente">Carnê Pendente</option>
            <option value="parcial">Carnê Parcial</option>
            <option value="pago">Carnê Pago</option>
            <option value="desistencia">Carnê — Desistência</option>
            <option value="cancelado">Carnê — Cancelado</option>
          </select>
          <label className="flex items-center gap-2 px-4 py-2 bg-white border border-sky-200 rounded-lg cursor-pointer hover:bg-sky-50">
            <input
              type="checkbox"
              checked={apenasCarne}
              onChange={(e) => setApenasCarne(e.target.checked)}
              className="w-4 h-4 text-sky-600 rounded focus:ring-sky-500"
            />
            <span className="text-sm text-gray-700 font-medium">Apenas Carnê</span>
          </label>
          {filtroViagem && (
            <button
              type="button"
              onClick={handleZerarViagem}
              disabled={zerando}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2 text-sm font-medium"
              title="Remove todos os pagamentos e parcelas desta viagem. Use para iniciar novo ciclo no mesmo destino."
            >
              <FiTrash2 size={18} />
              {zerando ? 'Zerando...' : 'Zerar dados desta viagem'}
            </button>
          )}
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-sky-50 rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-sky-200">
              <thead className="bg-sky-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Passageiro</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">CPF</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Viagem</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Valor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Status Carnê</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Data Pagamento</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-sky-200">
                {pagamentos.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 text-center text-gray-600 bg-white">
                      Nenhum pagamento encontrado
                    </td>
                  </tr>
                ) : (
                  pagamentos.map((pagamento) => {
                    const isCarne = pagamento.status_carne !== null && pagamento.status_carne !== undefined;
                    return (
                      <tr key={pagamento.id} className={`hover:bg-sky-100 bg-white ${isCarne ? 'border-l-4 border-purple-500' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {pagamento.passageiro_nome}
                          {isCarne && (
                            <span className="ml-2 text-xs text-purple-600 font-semibold">📋 Carnê</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{pagamento.cpf}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{pagamento.viagem_titulo}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          <div className="flex flex-col">
                            <span className="font-semibold">R$ {parseFloat(pagamento.valor).toFixed(2)}</span>
                            {pagamento.valor_pago_parcelas > 0 && (
                              <span className="text-xs text-blue-600">
                                Pago: R$ {pagamento.valor_pago_parcelas.toFixed(2)}
                              </span>
                            )}
                            {isCarne && pagamento.valor_a_receber > 0 && (
                              <span className="text-xs text-yellow-600">
                                A receber: R$ {pagamento.valor_a_receber.toFixed(2)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              pagamento.status === 'reembolsado'
                                ? 'bg-rose-100 text-rose-900'
                                : pagamento.status === 'pago' || pagamento.status_efetivo === 'pago'
                                ? 'bg-green-100 text-green-800'
                                : pagamento.status === 'desistencia' || pagamento.status_efetivo === 'desistencia'
                                ? 'bg-orange-100 text-orange-900'
                                : pagamento.status === 'cancelado'
                                ? 'bg-gray-200 text-gray-800'
                                : pagamento.status_efetivo === 'parcial' || pagamento.valor_pago_parcelas > 0
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {pagamento.status_efetivo === 'parcial'
                              ? 'Parcial'
                              : pagamento.status === 'desistencia' || pagamento.status_efetivo === 'desistencia'
                              ? 'Desistência'
                              : pagamento.status === 'reembolsado'
                              ? 'Reembolsado'
                              : pagamento.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isCarne ? (
                            <div className="flex flex-col gap-1">
                              <span
                                className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                  pagamento.status_carne === 'pago'
                                    ? 'bg-green-100 text-green-800'
                                    : pagamento.status_carne === 'desistencia'
                                    ? 'bg-orange-100 text-orange-900'
                                    : pagamento.status_carne === 'cancelado'
                                    ? 'bg-gray-200 text-gray-800'
                                    : pagamento.status_carne === 'parcial'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}
                              >
                                {pagamento.status_carne === 'pago'
                                  ? 'Pago'
                                  : pagamento.status_carne === 'desistencia'
                                  ? 'Desistência'
                                  : pagamento.status_carne === 'cancelado'
                                  ? 'Cancelado'
                                  : pagamento.status_carne === 'parcial'
                                  ? 'Parcial'
                                  : 'Pendente'}
                              </span>
                              {pagamento.total_parcelas > 0 && (
                                <span className="text-xs text-gray-600">
                                  {pagamento.parcelas_pagas || 0}/{pagamento.total_parcelas} parcelas
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {pagamento.data_pagamento ? formatarData(pagamento.data_pagamento) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {!isCarne && pagamento.status === 'pago' ? (
                            <button
                              type="button"
                              onClick={() => handleReembolsarPagamentoAvista(pagamento)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-xs font-medium"
                              title="Extorno no sistema (pagamento à vista)"
                            >
                              <FiRotateCcw size={14} />
                              Extorno
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Pagamentos;
